// Client-side photo compression. Images are stored as data URLs in
// Firestore (no Storage bucket needed on the free plan), so they must be
// compressed to fit comfortably inside Firestore's 1MB document limit.

const DOC_BUDGET = 850_000; // bytes of data-URL, leaves headroom in the doc

function render(bitmap: ImageBitmap, maxDim: number, quality: number): string {
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * Compress an image file to a JPEG data URL under the Firestore size budget.
 * Steps down quality, then dimensions, until it fits.
 */
export async function compressImage(file: File, maxDim = 1600): Promise<string> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(`Couldn't read "${file.name}" — use a JPG, PNG or WebP photo.`);
  }
  try {
    for (const dim of [maxDim, 1280, 1024, 800]) {
      for (const quality of [0.82, 0.7, 0.58]) {
        const out = render(bitmap, dim, quality);
        if (out.length <= DOC_BUDGET) return out;
      }
    }
    throw new Error(`"${file.name}" couldn't be compressed enough — try a smaller photo.`);
  } finally {
    bitmap.close();
  }
}
